import React, { useEffect, useState } from "react";
import PrimaryModal from "../UI/Modal/PrimaryModal";
import {
    editorKey,
    EditorPlugin,
    EditorToolBar,
} from "../../config/RichtextEditor";
import { PrimaryButton, SecondaryButton } from "../UI";
import { Editor } from "@tinymce/tinymce-react";
import { decodeHtmlEntities, rgbToHex } from "../../utils/helper";
import ColorPicker from "react-best-gradient-color-picker";
import { useFormik } from "formik";
import Input from "../UI/Input";
import DeleteIcon from '../../assets/icons/delete.svg';
import * as Yup from 'yup';
import { useContentfulContext } from "../../contexts/ContentfulContext";

const ManageEditInputModal = ({
    showEditor,
    handleHideModal,
    element,
    handleSave,
}) => {
    const [inputValue, setInputValue] = useState();
    const [dynamicFieldInputs, setDynamicFieldInputs] = useState({
        input_default_value: "",
        dynamic_field: ""
    });
    const [errors, setErrors] = useState({
        input_default_value: "",
        dynamic_field: "",
        input_options: "",
    });
    const [fields, setFields] = useState([]);
    const { content } = useContentfulContext();

    useEffect(() => {
        if (element) {
            setErrors({
                input_default_value: "",
                dynamic_field: "",
                input_options: "",
            });

            if (element.input.input_type === "dynamic-text") {
                setDynamicFieldInputs({
                    input_default_value: element?.value?.input_default_value || "",
                    dynamic_field: element?.value?.dynamic_field || "",
                });
            } else if (element.input.input_type === "array") {
                let fieldsArray = [];
                const initialValue = element.value;
                if (Array.isArray(initialValue)) {
                    fieldsArray = initialValue;
                } else if (typeof initialValue === "string" && initialValue !== "") {
                    fieldsArray = initialValue.split(",");
                }
                setFields(fieldsArray);
                formik.setFieldValue("inputFields", fieldsArray);
            } else if (element.input.input_type === "static-options" && element.colId === "input_default_value") {
                setDynamicFieldInputs((prev) => ({
                    ...prev,
                    input_default_value: element.value || "",
                }));
                let optionsArray = [];
                const initialOptions = element.input.input_options;
                if (Array.isArray(initialOptions)) {
                    optionsArray = initialOptions;
                } else if (typeof initialOptions === "string" && initialOptions !== "") {
                    optionsArray = initialOptions.split(",");
                }
                setFields(optionsArray);
                formik.setFieldValue("inputFields", optionsArray);
            } else {
                setInputValue(element.value || "");
            }
        }
    }, [element]);

    const handleSubmitDescription = () => {
        const singleLine = `${inputValue}`.replace(/\n/g, "").replaceAll(
            `<span class=\"mce-nbsp-wrap\" contenteditable=\"false\">&nbsp;</span>`,
            `&nbsp;`
        );
        const decoded = decodeHtmlEntities(singleLine);
        handleSave(element.rowIndex, element.colId, decoded);
    };

    function convertGradientRgbToHex(str) {
        return str.replace(/rgba?\([^)]+\)/gi, match => rgbToHex(match));
    }

    const handleSubmitColor = () => {
        let hexColor = inputValue;
        if (typeof inputValue === 'string' && inputValue.includes('rgb')) {
            hexColor = convertGradientRgbToHex(inputValue);
        }
        handleSave(element.rowIndex, element.colId, hexColor);
    };

    const handleSubmitDynamicText = () => {
        if (!dynamicFieldInputs.input_default_value.trim()) {
            setErrors((prev) => ({ ...prev, input_default_value: "Input Default Value is required" }));
            return;
        }
        if (!dynamicFieldInputs.dynamic_field.trim()) {
            setErrors((prev) => ({ ...prev, dynamic_field: "Dynamic Field is required" }));
            return;
        }
        setErrors({ input_default_value: "", dynamic_field: "", input_options: "" });
        handleSave(
            element.rowIndex,
            element.colId,
            dynamicFieldInputs.input_default_value.trim(),
            dynamicFieldInputs.dynamic_field.trim()
        );
    };

    const handleSubmitStaticOptions = () => {
        const trimmedOptions = fields.map(f => f.trim()).filter(f => f !== "");

        if (element.rules.is_mandatory && trimmedOptions.length === 0) {
            setErrors((prev) => ({ ...prev, input_options: content.STATIC_OPTION_FIELD_TYPE_CONTENT.error.option_required }));
            return;
        }

        if (element.rules.is_mandatory && !dynamicFieldInputs.input_default_value.trim()) {
            setErrors((prev) => ({ ...prev, input_default_value: content.STATIC_OPTION_FIELD_TYPE_CONTENT.error.default_value }));
            return;
        }

        if (
            dynamicFieldInputs.input_default_value.trim() &&
            !trimmedOptions.includes(dynamicFieldInputs.input_default_value.trim())
        ) {
            setErrors((prev) => ({
                ...prev,
                input_default_value: content.STATIC_OPTION_FIELD_TYPE_CONTENT.error.provided_options
            }));
            return;
        }

        setErrors({});
        handleSave(
            element.rowIndex,
            element.colId,
            dynamicFieldInputs.input_default_value.trim(),
            null,
            { input_options: trimmedOptions }
        );
    };

    const formik = useFormik({
        initialValues: {
            inputFields: fields,
        },
        validationSchema: Yup.object({
            inputFields:
                element?.input.input_type === "array" ||
                    (element.input.input_type === "static-options" && element.colId === "input_default_value")
                    ? Yup.array().of(
                        Yup.string()
                            .trim()
                            .min(+element.rules.min_length || 0, `${content.ARRAY_FIELD_TYPE_CONTENT.error.FE130} ${+element.rules.min_length || 50}`)
                            .max(+element.rules.max_length || 50, `${content.ARRAY_FIELD_TYPE_CONTENT.error.FE131} ${+element.rules.max_length || 50}`)
                            .required(content.ARRAY_FIELD_TYPE_CONTENT.error.FE129)
                    )
                    : Yup.array().of(
                        Yup.string()
                            .trim()
                            .min(+element.rules.min_length || 0, `${content.ARRAY_FIELD_TYPE_CONTENT.error.FE130} ${+element.rules.min_length || 50}`)
                            .max(+element.rules.max_length || 50, `${content.ARRAY_FIELD_TYPE_CONTENT.error.FE131} ${+element.rules.max_length || 50}`)
                    )
        }),
        onSubmit: (values) => {
            const trimmedFields = values.inputFields.map(v => v.trim()).filter(v => v !== "");

            if (element.input.input_type === "array") {
                if (element.rules.is_mandatory && trimmedFields.length === 0) {
                    setErrors((prev) => ({ ...prev, input_options: "At least one value is required." }));
                    return;
                }
                handleSave(element.rowIndex, element.colId, trimmedFields);
            }
        }
    });

    const handleAddField = () => {
        setFields([...fields, ""]);
        formik.setFieldValue("inputFields", [...fields, ""]);
    };

    const handleDeleteField = (index) => {
        const updatedFields = fields.filter((_, i) => i !== index);
        setFields(updatedFields);
        formik.setFieldValue("inputFields", updatedFields);
    };

    const handleFieldChange = (index, e) => {
        const updatedFields = fields.map((field, i) =>
            i === index ? e.target.value : field
        );
        setFields(updatedFields);
        formik.setFieldValue("inputFields", updatedFields);
    };

    return (
        <>
            {showEditor && (
                <PrimaryModal title={element.input.input_label || element.colId} hideModal={handleHideModal}>
                    {element.input.input_type === "rich-textarea" && (
                        <>
                            <div className="p-6 space-y-6">
                                <p className="text-base leading-tight tracking-tight text-black mb-4">
                                    {content.INPUT_TEXT_AREA_CONTENT.header}
                                </p>
                                <Editor
                                    apiKey={editorKey}
                                    onEditorChange={(e) => setInputValue(e)}
                                    value={inputValue}
                                    disabled={false}
                                    init={{
                                        menubar: true,
                                        plugins: EditorPlugin,
                                        toolbar: EditorToolBar,
                                    }}
                                />
                                <div className="flex items-center justify-end space-x-2">
                                    <PrimaryButton handleClick={handleSubmitDescription}>
                                        {content.INPUT_TEXT_AREA_CONTENT.save_btn}
                                    </PrimaryButton>
                                </div>
                            </div>
                        </>
                    )}

                    {element.input.input_type === "color-picker" && (
                        <>
                            <div className="p-6 space-y-6">
                                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                                    <div className="w-full max-w-xl flex flex-col items-center rounded-lg bg-white px-10 py-6 shadow-lg">
                                        <h2 className="mb-4 text-lg font-semibold self-start">
                                            {content.INPUT_COLOR_PICKER_CONTENT.title}
                                        </h2>
                                        <div className="max-h-[550px] overflow-hidden scrollbar-width-2px flex items-center">
                                            <ColorPicker value={inputValue} onChange={setInputValue} hidePresets={true} width={510} />
                                        </div>
                                        <div className="mt-4 flex justify-end gap-2 self-end">
                                            <button
                                                className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                                onClick={handleHideModal}
                                            >
                                                {content.INPUT_COLOR_PICKER_CONTENT.cancel_btn}
                                            </button>
                                            <button
                                                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-2 focus:outline-none focus:ring-2 focus:ring-primary-2 focus:ring-offset-2"
                                                onClick={handleSubmitColor}
                                            >
                                                {content.INPUT_COLOR_PICKER_CONTENT.pick_btn}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {element.input.input_type === "array" && (
                        <div className="mb-4">
                            <div className="p-6 space-y-6">
                                <form onSubmit={formik.handleSubmit}>
                                    {formik?.values?.inputFields?.map((fieldValue, index) => (
                                        <div className="mb-5" key={index}>
                                            <div className="relative mb-2">
                                                <input
                                                    type="text"
                                                    name={`inputFields[${index}]`}
                                                    className="block px-2.5 pb-2.5 pt-4 w-full pr-10 text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-2 ring-primary peer"
                                                    placeholder=" "
                                                    value={fieldValue}
                                                    onChange={(e) => handleFieldChange(index, e)}
                                                    onBlur={formik.handleBlur}
                                                />
                                                <button
                                                    type="button"
                                                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                                    onClick={() => handleDeleteField(index)}
                                                >
                                                    <img className="h-[22px] w-[22px]" src={DeleteIcon || "/placeholder.svg"} alt="Delete" />
                                                </button>
                                            </div>
                                            {formik.touched.inputFields &&
                                                formik.touched.inputFields[index] &&
                                                formik.errors.inputFields &&
                                                formik.errors.inputFields[index] && (
                                                    <p className="mt-2 text-sm text-red-500">{formik.errors.inputFields[index]}</p>
                                                )}
                                        </div>
                                    ))}
                                    <div className="flex items-center justify-end space-x-2">
                                        <SecondaryButton type="button" handleClick={handleAddField}>
                                            {content.ARRAY_FIELD_TYPE_CONTENT.add_field_cta}
                                        </SecondaryButton>
                                        <PrimaryButton type="submit">{content.ARRAY_FIELD_TYPE_CONTENT.save_btn}</PrimaryButton>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {element.input.input_type === "static-options" && element.colId === "input_default_value" && (
                        <div className="mb-4">
                            <div className="p-6 space-y-6">
                                <h3 className="text-lg font-semibold mb-4">{content.STATIC_OPTION_FIELD_TYPE_CONTENT.header}</h3>
                                <form onSubmit={(e) => e.preventDefault()}>
                                    <div className="mb-6 border p-4 rounded-md">
                                        <h4 className="text-md font-medium mb-3">Static Options</h4>
                                        {formik?.values?.inputFields?.map((fieldValue, index) => (
                                            <div className="mb-3" key={index}>
                                                <div className="relative mb-2">
                                                    <input
                                                        type="text"
                                                        name={`inputFields[${index}]`}
                                                        className="block px-2.5 pb-2.5 pt-4 w-full pr-10 text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-2 ring-primary peer"
                                                        placeholder=" "
                                                        value={fieldValue}
                                                        onChange={(e) => handleFieldChange(index, e)}
                                                        onBlur={formik.handleBlur}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                                        onClick={() => handleDeleteField(index)}
                                                    >
                                                        <img className="h-[22px] w-[22px]" src={DeleteIcon || "/placeholder.svg"} alt="Delete" />
                                                    </button>
                                                </div>
                                                {formik.touched.inputFields &&
                                                    formik.touched.inputFields[index] &&
                                                    formik.errors.inputFields &&
                                                    formik.errors.inputFields[index] && (
                                                        <p className="mt-2 text-sm text-red-500">{formik.errors.inputFields[index]}</p>
                                                    )}
                                            </div>
                                        ))}
                                        <SecondaryButton type="button" handleClick={handleAddField}>
                                           {content.STATIC_OPTION_FIELD_TYPE_CONTENT.add_option_cta}
                                        </SecondaryButton>
                                        {errors.input_options && <p className="mt-2 text-sm text-red-500">{errors.input_options}</p>}
                                    </div>

                                    <div className="mb-4 border p-4 rounded-md">
                                        <h4 className="text-md font-medium mb-3">{content.STATIC_OPTION_FIELD_TYPE_CONTENT.select_default_value}</h4>
                                        {fields && fields.length > 0 ? (
                                            <div className="space-y-2">
                                                {fields.map((option, index) => (
                                                    <div key={index} className="flex items-center">
                                                        <input
                                                            type="radio"
                                                            id={`radio-${index}`}
                                                            name="default_static-options_value"
                                                            value={option}
                                                            checked={dynamicFieldInputs.input_default_value === option}
                                                            onChange={(e) =>
                                                                setDynamicFieldInputs((prev) => ({
                                                                    ...prev,
                                                                    input_default_value: e.target.value,
                                                                }))
                                                            }
                                                            className="h-4 w-4 text-primary border-gray-300 focus:ring-primary-2"
                                                        />
                                                        <label htmlFor={`radio-${index}`} className="ml-2 text-sm text-gray-900">
                                                            {option}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-500">{content.STATIC_OPTION_FIELD_TYPE_CONTENT.add_above_option}</p>
                                        )}
                                        {errors.input_default_value && (
                                            <p className="mt-2 text-sm text-red-500">{errors.input_default_value}</p>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-end space-x-2 mt-6">
                                        <SecondaryButton handleClick={handleHideModal}>{"Cancel"}</SecondaryButton>
                                        <PrimaryButton type="button" handleClick={handleSubmitStaticOptions}>
                                           {content.STATIC_OPTION_FIELD_TYPE_CONTENT.save_btn}
                                        </PrimaryButton>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {element.input.input_type === "dynamic-text" && (
                        <div className="mb-4">
                            <div className="p-6 space-y-4">
                                <Input
                                    id="input_default_value"
                                    name="input_default_value"
                                    label={content.INPUT_DYNAMIC_TEXT_CONTENT.form.input_default_value}
                                    value={dynamicFieldInputs.input_default_value}
                                    onChange={(e) =>
                                        setDynamicFieldInputs((prev) => ({ ...prev, input_default_value: e.target.value }))
                                    }
                                    error={errors.input_default_value}
                                />
                                <Input
                                    id="dynamic_field"
                                    name="dynamic_field"
                                    label={content.INPUT_DYNAMIC_TEXT_CONTENT.form.dynamic_field}
                                    value={dynamicFieldInputs.dynamic_field}
                                    onChange={(e) => setDynamicFieldInputs((prev) => ({ ...prev, dynamic_field: e.target.value }))}
                                    error={errors.dynamic_field}
                                />
                                <div className="flex items-center justify-end space-x-2 pt-2">
                                    <SecondaryButton handleClick={handleHideModal}>{"Cancel"}</SecondaryButton>
                                    <PrimaryButton handleClick={handleSubmitDynamicText}>
                                        {content.INPUT_DYNAMIC_TEXT_CONTENT.form.save_btn}
                                    </PrimaryButton>
                                </div>
                            </div>
                        </div>
                    )}
                </PrimaryModal>
            )}
        </>
    )
};

export default ManageEditInputModal;